import {ReactComponent as Segmentation_people} from './Layer_images/Settings_images/segmentation_people.svg'
import {ReactComponent as Segmentation_calendar} from './Layer_images/Settings_images/segmentation_calendar.svg'
import {ReactComponent as Segmentation_dollar} from './Layer_images/Settings_images/segmentation_dollar.svg'
import populationSegmentation from './json/population_segments.json';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {SegmentationPopupDemographic} from './ui_components/segmentationPopupDemographic'
import {SegmentationPopupInterest} from './ui_components/segmentationPopupInterest'
import {SegmentationPopupChartBox} from './ui_components/segmentationPopupChartBox'
import {SegmentationPopupLocations} from './ui_components/segmentationPopupLocations'

const age_mappings = {
  'under_18': 'Youth',
  '18_24': 'Young Adults',
  '25_34': 'Establishing Adults',
  '35_44': 'Mid-Career Adults',
  '45_54': 'Established Adults',
  '55_64': 'Pre-Retiree',
  '65_plus': 'Retired'
}

const age_segments = {
  'under_18': 'Under 18',
  '18_24': '18 - 24',
  '25_34': '25 - 34',
  '35_44': '35 - 44',
  '45_54': '45 - 54',
  '55_64': '55 - 64',
  '65_plus': '65+'
}

const income_segments = {
  'low_income': '$0-30,000',
  'lower_middle_income': '$30,000-50,000',
  'middle_income': '$50,000-75,000',
  'upper_middle_income': '$75,000-125,000',
  'high_income': '$125,000-200,000',
  'very_high_income': '$200,000+'
}

const income_mapping = {
  low_income: 'Essential Income',
  lower_middle_income: 'Building Income',
  middle_income: 'Stable Income',
  upper_middle_income: 'Comfortable Income',
  high_income: 'Affluent Income',
  very_high_income: 'Elite Income',
}

const region_mapping = {
  'Urban Core': 'Metro',
  Urban: 'City',
  Suburban: 'Suburbs',
  Rural: 'Rural'
}

const frequencyMapping = (frequencyValue) => {
  if (frequencyValue < 0.5) {
    return "Rarely or Never";
  } else if (frequencyValue >= 0.5 && frequencyValue < 1) {
    return "Few Times a Year";
  } else if (frequencyValue >= 1 && frequencyValue < 1.5) {
    return "Monthly";
  } else if (frequencyValue >= 1.5 && frequencyValue < 2.5) {
    return "Bi-Weekly";
  } else if (frequencyValue >= 2.5 && frequencyValue < 3.5) {
    return "Almost Weekly";
  } else if (frequencyValue >= 3.5) {
    return "Weekly or More";
  }
}

export function SegmentationPopup({segmentationMain, segmentation, segmentationClose}) {
  const [tabValue, setTabValue] = useState(0);
  const [segmentData, setSegmentData] = useState([]);

  // Memoize the processItems function
  const processItems = useCallback((items, getFrequency=false) => {
    if (!items || !Array.isArray(items)) return items;
    
    return items.map(item => {
      // Create a new object instead of modifying the original
      const newItem = { ...item };
      
      if (newItem.segmentation && newItem.segmentation.segmentOverview) {
        if(getFrequency) {
          newItem.value = frequencyMapping(newItem.segmentation.frequency);
        } else {
          const segmentKey = Object.keys(newItem.segmentation.segmentOverview)[0];
          if (segmentKey) {
            newItem.value = newItem.segmentation.segmentOverview[segmentKey];
          }
        }
      }
      return newItem;
    });
  }, []);

  // Memoize the generateColorScale function
  const generateColorScale = useCallback((data, dataType) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      // Default color scale if no data is available
      if(dataType === 'retail'){
        return [
          { range: [0, 100], color: "#d1e7ff", label: "0-100" },
          { range: [100, 200], color: "#92c5fc", label: "100-200" },
          { range: [200, 300], color: "#3a8bfd", label: "200-300" },
          { range: [300, 400], color: "#0056b3", label: "300-400" },
          { range: [400, 500], color: "#003f7f", label: "400-500" }
        ];
      } else {
        return [
          { range: [0, 100], color: "#FFF1D6", label: "0-100" },
          { range: [100, 200], color: "#FFD78A", label: "100-200" },
          { range: [200, 300], color: "#FFBA45", label: "200-300" },
          { range: [300, 400], color: "#D9853B", label: "300-400" },
          { range: [400, 500], color: "#943A00", label: "400-500" }
        ];
      }
    }
    
    // Extract values, filter out undefined or NaN
    const values = data
      .map(item => item.value)
      .filter(value => value !== undefined && !isNaN(value));
    
    if (values.length === 0) {
      return [
        { range: [0, 100], color: "#d1e7ff", label: "0-100" }
      ];
    }
    
    // Find min and max values
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    
    // Round to nearest 100
    const roundedMin = Math.floor(minValue / 100) * 100;
    const roundedMax = Math.ceil(maxValue / 100) * 100;
    
    // Create 5 ranges or fewer if the range is small
    const totalRange = roundedMax - roundedMin;
    const stepCount = Math.min(5, Math.max(1, Math.ceil(totalRange / 100)));
    const step = Math.ceil(totalRange / stepCount / 100) * 100;
    
    const retailColors = ["#d1e7ff", "#92c5fc", "#3a8bfd", "#0056b3", "#003f7f"];
    const foodColors = ["#FFF1D6", "#FFD78A", "#FFBA45", "#D9853B", "#943A00"];
    
    const colorScale = [];
    
    for (let i = 0; i < stepCount; i++) {
      const start = roundedMin + (i * step);
      const end = i === stepCount - 1 ? roundedMax : roundedMin + ((i + 1) * step);
      
      colorScale.push({
        range: [start, end],
        color: dataType === 'retail' ? retailColors[i] : foodColors[i],
        label: `${start}-${end}`
      });
    }
    
    return colorScale;
  }, []);

  // Memoize the color scales for retail and food locations
  const retailColorScale = useMemo(() => 
    generateColorScale(segmentData.locations_retail, 'retail'),
    [segmentData.locations_retail, generateColorScale]
  );
  
  const foodColorScale = useMemo(() => 
    generateColorScale(segmentData.locations_food, 'food'),
    [segmentData.locations_food, generateColorScale]
  );

  const getSegmentationData = useCallback(async () => {
    let token = localStorage.getItem("auth-token");
    try {
      let response = await axios.post('/gettheurl/segmentation-click', {
        pumaId: segmentation.pumaId,
        bounds_ids: segmentation.bounds_ids,
        GEO_values: segmentation.GEO_values,
        segmentationMain,
        bounds: ""
      }, {
        headers: { "auth-token": token }
      });

      const res_data = response.data;
      
      // Create a new object to avoid mutations
      const processedData = { ...res_data };
      
      if (processedData.locations_food) {
        processedData.locations_food = processItems(processedData.locations_food);
      }
      
      if (processedData.locations_retail) {
        processedData.locations_retail = processItems(processedData.locations_retail);
      }

      // Fix inconsistent naming
      if (processedData.location_retail) {
        processedData.locations_retail = processedData.location_retail;
        delete processedData.location_retail;
      }
      
      if (processedData.locations_overall) {
        processedData.locations_overall = processItems(processedData.locations_overall, true);
      }
      
      setSegmentData(processedData);
    } catch (error) {
      console.error("Error fetching segmentation data:", error);
    }
  }, [segmentation, segmentationMain, processItems]);

  // Call the API when segmentationMain changes
  useEffect(() => {
    getSegmentationData();
  }, [segmentationMain, getSegmentationData]);

  function formatNumber(value) {
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
      return Number(value).toLocaleString();
    } else {
      return value;
    }
  }

  const getDescription = useCallback((age_group, income_group, region_type) => {
    if (age_group === 'under_18') {
      income_group = 'low_income';
    }
    
    const key = `${age_group}_${income_group}_${region_type}`;
    return populationSegmentation[key]?.description || '';
  }, []);

  const get_image_link = useCallback((age, income) => {
    let income_value = 1;
    if (age !== 'under_18') {
      const income_mapping = { 
        'low_income': 1, 
        'lower_middle_income': 2, 
        'middle_income': 3, 
        'upper_middle_income': 4, 
        'high_income': 5,  
        'very_high_income': 6
      };
      income_value = income_mapping[income];
    }
    
    return `segmentation/${age}_${income_value}.jpeg`;
  }, []);
  
  return (
    <div className="task-search-background">
      <div className="Psychographic-total">  
        <div>
          <div className='sidebar-realestate-total'>
            <div className='display-flex gap-10 sidbar-primary-text font-size-16'>
              {segmentationMain.age_group && 
                <div className='display-flex gap-10'>
                  <div>{age_mappings[segmentationMain.age_group]}</div>
                  <div>•</div>
                </div>
              }
              {segmentationMain.income_group && 
                <div className='display-flex gap-10'>
                  <div>{income_mapping[segmentationMain.income_group]}</div>
                  <div>•</div>
                </div>
              }
              {segmentationMain.region_type && 
                <div className='display-flex gap-10'>
                  <div>{region_mapping[segmentationMain.region_type]}</div>
                </div>
              }
              <div 
                className='display-flex gap-5 cursor-pointer font-size-14' 
                style={{ display: 'flex', marginLeft: 'auto', alignItems: 'center', gap: 5, color: '#535862'}} 
                onClick={() => segmentationClose()}
              >
                <span>Close</span>
                <i className="fa fa-times-circle"></i>
              </div>        
            </div>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', margin: "15px 0 25px 0"}}>
              <Tabs 
                value={tabValue} 
                onChange={(event, newValue) => setTabValue(newValue)} 
                aria-label="Popup tabs"
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    fontSize: '13px',
                    minWidth: 0  
                  }
                }}
              >
                <Tab label="Overview" />
                <Tab label="Demographic" />
                <Tab label="Interests" />
                <Tab label="In-Store Visits" />
              </Tabs>
            </Box>
            {tabValue === 0 && (
              <div>
                <div className='display-flex gap-10 mtb-10 sidbar-primary-text'>
                  <img 
                    src={get_image_link(segmentationMain.age_group, segmentationMain.income_group)} 
                    className='segmentation-profile-image'
                  />
                  <div className="box-container">
                    <div className='segmentation-top-sidebar'>
                      <div className='display-inline-block pt-5'><Segmentation_people/></div>
                      <div className='display-inline-block font-size-10'>Number of People</div>
                    </div>
                    <div className='text-align-left pl-5' style={{color: '#1570EF'}}>
                      {formatNumber(segmentationMain.total_pop)}
                    </div>
                  </div>
                  <div className="box-container">
                    <div className='segmentation-top-sidebar'>
                      <div className='display-inline-block pt-5'><Segmentation_calendar/></div>
                      <div className='display-inline-block font-size-10'>Age Group</div>
                    </div>
                    <div className='text-align-left pl-5'>
                      {age_segments[segmentationMain.age_group]}
                    </div>
                  </div>
                  <div className="box-container">
                    <div className='segmentation-top-sidebar'>
                      <div className='display-inline-block pt-5'><Segmentation_dollar/></div>
                      <div className='display-inline-block font-size-10'>Income Group</div>
                    </div>
                    <div className='text-align-left pl-5'>
                      {income_segments[segmentationMain.income_group]}
                    </div>
                  </div>
                </div>
                <div className='segmentation-text-sidebar mt-20'>
                  {getDescription(segmentationMain.age_group, segmentationMain.income_group, segmentationMain.region_type)}
                </div>
                <div className='display-grid grid-repeat gap-20 height-400'>
                  <SegmentationPopupChartBox 
                    data={segmentData.locations_retail} 
                    colorScale={retailColorScale} 
                    title={"Top Retail Visits"}
                  />
                  <SegmentationPopupChartBox 
                    data={segmentData.locations_food} 
                    colorScale={foodColorScale} 
                    title={"Top Restaurant Visits"}
                  />
                </div>
              </div>
            )}
            {tabValue === 1 && (
              <SegmentationPopupDemographic 
                segmentData={segmentData} 
                segmentationMain={segmentationMain} 
                region_mapping={region_mapping}
              />
            )}
            {tabValue === 2 && (
              <SegmentationPopupInterest segmentData={segmentData}/>
            )}
            {tabValue === 3 && (
              <SegmentationPopupLocations segmentData={segmentData}/>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}